import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import socketService from '../services/socket';

function Navbar() {
  const { user, logout } = useAuth();
  const [adminNotice, setAdminNotice] = useState(null);

  // When a restaurant-manager is operating an outlet, the portal runs on an
  // owner-scoped session but `manager_token` + `managing_restaurant` are kept so
  // they can switch outlets / log out cleanly.
  const managingRestaurant = (() => {
    try { return JSON.parse(localStorage.getItem('managing_restaurant') || 'null'); } catch (_) { return null; }
  })();
  const isManaging = !!localStorage.getItem('manager_token') && !!managingRestaurant;

  const switchOutlet = () => {
    const mt = localStorage.getItem('manager_token');
    if (mt) localStorage.setItem('token', mt);
    localStorage.removeItem('managing_restaurant');
    window.location.href = '/restaurant-manager/select';
  };

  const handleLogout = async () => {
    const wasManaging = !!localStorage.getItem('manager_token');
    await logout();
    localStorage.removeItem('manager_token');
    localStorage.removeItem('managing_restaurant');
    // Managers return to their own login; everyone else to the main login.
    window.location.href = wasManaging ? '/restaurant-manager/login' : '/login';
  };

  // Admins receive LIVE notifications (e.g. a support member changing an order's
  // status) via the `admin` socket room. Show a transient toast.
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    if (!socketService.isConnected) {
      socketService.connect(localStorage.getItem('token'));
    }
    const unsubscribe = socketService.on('admin_notification', (data) => {
      if (!data) return;
      setAdminNotice(data);
      setTimeout(() => setAdminNotice((cur) => (cur === data ? null : cur)), 9000);
    });
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [user]);

  if (!user) return null;

  const getNavLinks = () => {
    switch (user.role) {
      case 'user':
        return (
          <>
            <Link to="/user/home">Home</Link>
            <Link to="/user/orders">My Orders</Link>
            <Link to="/user/profile">Profile</Link>
            <Link to="/help">Help & Support</Link>
          </>
        );
      case 'restaurant':
        return (
          <>
            {isManaging && (
              <span
                onClick={switchOutlet}
                title="Switch to another outlet"
                style={{ cursor: 'pointer', background: '#FFF1F0', color: '#FF6961', border: '1px solid #FFD9D6', borderRadius: 8, padding: '4px 10px', fontWeight: 700, fontSize: 13, marginRight: 6 }}
              >
                🏬 {managingRestaurant.name} · Switch ▾
              </span>
            )}
            <Link to="/restaurant/dashboard">Dashboard</Link>
            <Link to="/restaurant/menu">Menu</Link>
            <Link to="/restaurant/orders">Orders</Link>
            <Link to="/restaurant/coupons">Coupons</Link>
            <Link to="/restaurant/tomato-calculator">Tomato Payout</Link>
            <Link to="/restaurant/profile">Profile</Link>
            <Link to="/help">Help & Support</Link>
          </>
        );
      case 'delivery':
        return (
          <>
            <Link to="/delivery/dashboard">Dashboard</Link>
            <Link to="/delivery/available">Available Orders</Link>
            <Link to="/delivery/my-deliveries">My Deliveries</Link>
            <Link to="/delivery/location">Location Tracker</Link>
            <Link to="/help">Help & Support</Link>
          </>
        );
      case 'admin':
        return (
          <>
            <Link to="/admin/dashboard">Dashboard</Link>
            <Link to="/admin/orders">Orders</Link>
            <Link to="/admin/live-map">Live Map</Link>
            <Link to="/admin/assignment-tracker">Assignment Tracker</Link>
            <Link to="/admin/surge-analytics">Surge Analytics</Link>
            <Link to="/admin/approvals">Delivery Approvals</Link>
            <Link to="/admin/delivery-partners">Delivery Partners</Link>
            <Link to="/admin/cod-autopay">COD Autopay</Link>
            <Link to="/admin/cod-settlement">COD Settlement</Link>
            <Link to="/admin/delivery-work-analysis">Work Analysis</Link>
            <Link to="/admin/delivery-distance">Delivery Distance</Link>
            <Link to="/admin/restaurant-reviews">Restaurant Reviews</Link>
            <Link to="/admin/restaurants">Restaurant Management</Link>
            <Link to="/admin/restaurant-manager">Restaurant Manager</Link>
            <Link to="/admin/banner-slides">Banner Slides</Link>
            <Link to="/admin/home-priority">Home Priority</Link>
            <Link to="/admin/change-requests">Change Requests</Link>
            <Link to="/admin/daily-post">Daily Social Post</Link>
            <Link to="/admin/print-settings">Print Settings</Link>
            <Link to="/admin/attender">Virtual Attender</Link>
            <Link to="/admin/competitor-intel">Competitor Prices</Link>
            <Link to="/admin/vs-tomato">Tomato Calculator</Link>
            <Link to="/admin/tomato-payout">Tomato Payout</Link>
            <Link to="/admin/priority-zones">Priority Zones</Link>
            <Link to="/admin/cities">Cities</Link>
            <Link to="/admin/work-areas">Work Areas</Link>
            <Link to="/admin/slots">Slot Management</Link>
            <Link to="/admin/gig-builder">Gig Builder</Link>
            <Link to="/admin/milestones">Milestone Rewards</Link>
            <Link to="/admin/referral-teams">Referral Teams</Link>
            <Link to="/admin/adjustments">Manual Adjustments</Link>
            <Link to="/admin/performance">Performance</Link>
            <Link to="/admin/settings">Settings</Link>
            <Link to="/admin/platform-settings">Platform Settings</Link>
            <Link to="/admin/app-versions">App Versions</Link>
            <Link to="/admin/otp-provider">OTP Provider</Link>
            <Link to="/admin/payment-settings">Payment Settings</Link>
            <Link to="/admin/notification-sounds">Notification Sounds</Link>
            <Link to="/admin/customer-platform-fee">Platform Fee</Link>
            <Link to="/admin/invoices">Invoices</Link>
            <Link to="/admin/finance-audit">Finance Audit</Link>
            <Link to="/admin/finance-hub">Finance Hub</Link>
            <Link to="/admin/growth-diagnostics">Growth Diagnostics</Link>
            <Link to="/admin/personal-finance-hub">Personal Finance Hub</Link>
            <Link to="/admin/sales-export">Sales Export (Excel)</Link>
            <Link to="/admin/contacts-export">Contacts Export</Link>
            <Link to="/admin/user-insights">User Insights</Link>
            <Link to="/admin/cart-recovery">Cart Recovery</Link>
            <Link to="/admin/re-engagement">Re-engagement</Link>
            <Link to="/admin/ai-messaging">AI Messaging</Link>
            <Link to="/admin/daily-nudge">Daily Nudge</Link>
            <Link to="/admin/mithai-special">Mithai Special</Link>
            <Link to="/admin/local-famous-karts">Local Famous Karts</Link>
            <Link to="/admin/delivery-service">Delivery Service</Link>
            <Link to="/admin/refunds">Refunds</Link>
            <Link to="/admin/billing-models">Billing Models</Link>
            <Link to="/admin/wallet-settings">Wallet</Link>
            <Link to="/admin/wallet-analytics">Wallet Analytics</Link>
            <Link to="/admin/notifications">Notifications</Link>
            <Link to="/admin/coupons">Coupons</Link>
            <Link to="/admin/item-discounts">Item Discounts</Link>
            <Link to="/admin/addons">Addons</Link>
            <Link to="/admin/support-tickets">Support Tickets</Link>
            <Link to="/admin/support-team">Support Team</Link>
            <Link to="/admin/restaurant-managers">Restaurant Managers</Link>
            <Link to="/admin/infrastructure">Infrastructure</Link>
            <Link to="/admin/server-metrics">Server Metrics</Link>
            <Link to="/admin/rate-limits">Rate Limits</Link>
            <Link to="/help">Help & Support</Link>
          </>
        );
      case 'support':
        return (
          <>
            <Link to="/support-team/dashboard">Dashboard</Link>
            <Link to="/support-team/restaurant-manager">Restaurant Manager</Link>
            <Link to="/support-team/orders">Orders</Link>
            <Link to="/support-team/live-map">Live Map</Link>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <nav className="navbar">
      <Link to="/" className="navbar-brand">
        🍔 Foodwinc
      </Link>
      <div className="navbar-menu">
        {getNavLinks()}
        <span className="navbar-user">
          👋 {user.name}
        </span>
        <button onClick={handleLogout} className="btn navbar-logout-btn">
          🚪 Logout
        </button>
      </div>
      {adminNotice && (
        <div
          onClick={() => setAdminNotice(null)}
          style={{
            position: 'fixed', top: 16, right: 16, zIndex: 9999, cursor: 'pointer',
            maxWidth: 360, background: '#111827', color: '#fff', borderRadius: 10,
            padding: '12px 16px', boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
            borderLeft: '4px solid #f59e0b'
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
            🔔 {adminNotice.title || 'Update'}
          </div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>{adminNotice.message || ''}</div>
          <div style={{ fontSize: 10, opacity: 0.5, marginTop: 6 }}>tap to dismiss</div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
